import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import SubcontractorBadge from "../components/SubcontractorBadge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import {
  CaretLeft, Star, PencilSimple, ShieldCheck, FileText, Note,
  Trash, Plus, EnvelopeSimple, Phone, MapPin, Buildings, IdentificationBadge,
  CheckCircle, Warning, ArrowSquareOut, Bank, Receipt, FilePdf, User, UploadSimple,
  Eye, DownloadSimple, PaperPlaneTilt
} from "@phosphor-icons/react";
import { toast } from "sonner";
import SupplierBadgeRibbon from "../components/SupplierBadgeRibbon";
import { UkAreasPicker } from "../components/UkAreasPicker";
import { ServicesPicker } from "../components/ServicesPicker";
import SupplierTimesheetPanel from "../components/supplier/SupplierTimesheetPanel";
import { SupplierMarketplaceInvite } from "../components/marketplace/SupplierMarketplaceInvite";
import { fmtDate } from "../lib/dateUtil";

import DateInput from "../components/DateInput";
const DOC_TYPES = [
  "insurance", "public_liability", "employers_liability", "professional_indemnity",
  "gas_safe", "niceic", "napit", "chas", "safe_contractor", "constructionline",
  "dbs_check", "cscs_card", "asbestos_awareness", "first_aid", "fire_safety",
  "method_statement", "risk_assessment", "waste_carrier_license", "contract", "accreditation", "other"
];

function StatusBadge({ status }) {
  const map = {
    valid: { label: "Valid", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400" },
    expiring_soon: { label: "Expiring", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400" },
    expired: { label: "Expired", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400" },
    no_expiry: { label: "No Expiry", cls: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" },
  };
  const c = map[status] || map.no_expiry;
  return <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${c.cls}`}>{c.label}</span>;
}

function StarRating({ value, onChange, size = 18 }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <button key={i} type="button"
          onMouseEnter={() => onChange && setHover(i)}
          onMouseLeave={() => onChange && setHover(0)}
          onClick={() => onChange && onChange(i)}
          className={`transition-colors ${onChange ? "cursor-pointer" : "cursor-default"}`}
        >
          <Star size={size} weight={(hover || value) >= i ? "fill" : "regular"}
            className={(hover || value) >= i ? "text-amber-400" : "text-muted-foreground/30"} />
        </button>
      ))}
    </div>
  );
}

export default function SupplierDetailPage() {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ["super_admin", "admin", "project_manager"].includes(user?.role);

  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("details");

  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [nudgingDocId, setNudgingDocId] = useState(null);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [docForm, setDocForm] = useState({ doc_type: "insurance", document_number: "", description: "", issue_date: "", expiry_date: "", notes: "" });
  const [docFile, setDocFile] = useState(null);
  const [savingDoc, setSavingDoc] = useState(false);
  const docFileRef = useRef(null);

  const [ratings, setRatings] = useState({ ratings: [], averages: {}, total_ratings: 0 });
  const [showAddRating, setShowAddRating] = useState(false);
  const [ratingForm, setRatingForm] = useState({ quality: 0, reliability: 0, communication: 0, pricing: 0, overall: 0, comment: "", project_reference: "" });

  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");

  // Phase 244 — Best-Supplier Badges
  const [badges, setBadges] = useState([]);

  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showCompliance, setShowCompliance] = useState(false);
  const [complianceForm, setComplianceForm] = useState({ manually_compliant: false, compliance_notes: "" });

  const fetchVendor = useCallback(async () => {
    try {
      const { data } = await API.get(`/suppliers/${vendorId}`);
      setVendor(data);
      setComplianceForm({ manually_compliant: data.manually_compliant || false, compliance_notes: data.compliance_notes || "" });
    } catch { toast.error("Failed to load supplier"); }
    finally { setLoading(false); }
  }, [vendorId]);

  const fetchDocs = useCallback(async () => {
    setLoadingDocs(true);
    try { const { data } = await API.get(`/suppliers/${vendorId}/compliance`); setDocs(data); } catch {}
    finally { setLoadingDocs(false); }
  }, [vendorId]);

  const fetchRatings = useCallback(async () => {
    try { const { data } = await API.get(`/suppliers/${vendorId}/ratings`); setRatings(data); } catch {}
  }, [vendorId]);

  const fetchNotes = useCallback(async () => {
    try { const { data } = await API.get(`/suppliers/${vendorId}/notes`); setNotes(data); } catch {}
  }, [vendorId]);

  const fetchBadges = useCallback(async () => {
    try { const { data } = await API.get(`/suppliers/${vendorId}/badges`); setBadges(data?.badges || []); } catch {}
  }, [vendorId]);

  useEffect(() => { fetchVendor(); fetchDocs(); fetchRatings(); fetchNotes(); fetchBadges(); }, [fetchVendor, fetchDocs, fetchRatings, fetchNotes, fetchBadges]);

  const handleAddDoc = async () => {
    setSavingDoc(true);
    try {
      let fileData = {};
      if (docFile) {
        const formData = new FormData();
        formData.append("file", docFile);
        const { data: uploaded } = await API.post(`/upload?entity_type=compliance&entity_id=${vendorId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        fileData = { file_id: uploaded.file_id || uploaded.id, file_name: uploaded.file_name };
      }
      await API.post(`/suppliers/${vendorId}/compliance`, { ...docForm, ...fileData });
      toast.success("Document added");
      setShowAddDoc(false);
      setDocForm({ doc_type: "insurance", document_number: "", description: "", issue_date: "", expiry_date: "", notes: "" });
      setDocFile(null);
      fetchDocs();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSavingDoc(false); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try { await API.delete(`/compliance/${docId}`); toast.success("Deleted"); fetchDocs(); } catch { toast.error("Failed"); }
  };

  // View the attached file inline (images & PDFs render; other types download).
  // Phase 263 fix — supplier docs are stored via /api/upload → db.uploaded_files,
  // served by /api/files/{file_id}/{file_name}. Raw window.open() can't send
  // the Bearer header so we blob-fetch via axios (auth-aware) then open the
  // object URL.
  const handleViewDoc = async (doc) => {
    if (!doc.file_id) { toast.error("No file is attached to this document"); return; }
    const filename = doc.file_name || "document";
    try {
      const res = await API.get(
        `/files/${doc.file_id}/${encodeURIComponent(filename)}`,
        { responseType: "blob" },
      );
      const mime = res.data?.type || res.headers?.["content-type"] || "application/octet-stream";
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) {
        toast.error("Pop-ups blocked — using download instead");
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 404) toast.error("File record missing — please re-upload");
      else if (code === 410) toast.error("File data was lost — please re-upload");
      else if (code === 401 || code === 403) toast.error("Access denied to this file");
      else toast.error("Could not load the file");
    }
  };

  // Force-download the file with its original name (blob-fetch through axios
  // so the Bearer token is attached).
  const handleDownloadDoc = async (doc) => {
    if (!doc.file_id) { toast.error("No file is attached to this document"); return; }
    const filename = doc.file_name || `${doc.doc_type || "document"}-${doc.id}`;
    try {
      const res = await API.get(
        `/files/${doc.file_id}/${encodeURIComponent(filename)}`,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: res.data?.type || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 404) toast.error("File record missing — please re-upload");
      else if (code === 410) toast.error("File data was lost — please re-upload");
      else if (code === 401 || code === 403) toast.error("Access denied to this file");
      else toast.error("Download failed");
    }
  };

  // Phase 263 — send the supplier a one-click re-upload link via email
  const handleNudgeReupload = async (doc) => {
    if (!vendorId || !doc?.id) return;
    setNudgingDocId(doc.id);
    try {
      const { data } = await API.post(`/suppliers/${vendorId}/compliance/${doc.id}/nudge-reupload`);
      toast.success(`Re-upload link sent to ${data.to}`);
      await fetchDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the re-upload link");
    } finally { setNudgingDocId(null); }
  };

  const handleAddRating = async () => {
    if (!ratingForm.overall) { toast.error("Please set an overall rating"); return; }
    try {
      await API.post(`/suppliers/${vendorId}/ratings`, ratingForm);
      toast.success("Rating added");
      setShowAddRating(false);
      setRatingForm({ quality: 0, reliability: 0, communication: 0, pricing: 0, overall: 0, comment: "", project_reference: "" });
      fetchRatings(); fetchVendor();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleDeleteRating = async (ratingId) => {
    if (!window.confirm("Delete this rating?")) return;
    try { await API.delete(`/suppliers/${vendorId}/ratings/${ratingId}`); toast.success("Deleted"); fetchRatings(); fetchVendor(); } catch { toast.error("Failed"); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try { await API.post(`/suppliers/${vendorId}/notes`, { content: newNote }); setNewNote(""); fetchNotes(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleDeleteNote = async (noteId) => {
    try { await API.delete(`/suppliers/${vendorId}/notes/${noteId}`); fetchNotes(); } catch {}
  };

  const handleEditVendor = async () => {
    try {
      const payload = { ...editForm };
      if ((payload.areas_covered || []).length) payload.coverage_area = payload.areas_covered.join(", ");
      if ((payload.sectors || []).length) payload.trade = payload.sectors.join(", ");
      await API.put(`/suppliers/${vendorId}`, payload); toast.success("Profile updated"); setShowEdit(false); fetchVendor();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleComplianceUpdate = async () => {
    try { await API.put(`/suppliers/${vendorId}/compliance-status`, complianceForm); toast.success("Compliance updated"); setShowCompliance(false); fetchVendor(); }
    catch { toast.error("Failed"); }
  };

  const handleInvite = async () => {
    try { await API.post(`/suppliers/${vendorId}/invite`); toast.success("Invitation sent"); fetchVendor(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this supplier?")) return;
    try { await API.delete(`/suppliers/${vendorId}`); toast.success("Supplier deleted"); navigate("/vendors"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!vendor) return <div className="text-center py-20 text-muted-foreground">Supplier not found</div>;

  const sectorsDisplay = vendor.sectors?.length ? vendor.sectors : (vendor.trade ? [vendor.trade] : []);
  const areasDisplay = vendor.areas_covered?.length ? vendor.areas_covered : (vendor.coverage_area ? [vendor.coverage_area] : []);
  const statusLabel = vendor.status === "active" ? "Active" : vendor.status === "inactive" ? "Inactive" : vendor.status;
  const statusColor = vendor.status === "active" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-slate-100 text-slate-600";
  const docsExpiring = docs.filter(d => d.computed_status === "expiring_soon").length;
  const docsExpired = docs.filter(d => d.computed_status === "expired").length;
  const isCompliant = vendor.manually_compliant || (docs.length > 0 && docsExpired === 0);

  return (
    <div data-testid="supplier-detail-page">
      {/* ═══════ HEADER ═══════ */}
      <div className="flex items-start gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/vendors")} className="mt-1 shrink-0" data-testid="back-to-suppliers">
          <CaretLeft size={20} />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight uppercase" data-testid="supplier-name">
              {vendor.company_name}
            </h1>
            <Badge className={`text-[10px] font-semibold ${statusColor}`} data-testid="supplier-status">{statusLabel}</Badge>
            <SubcontractorBadge supplier={vendor} />
            {vendor.average_rating > 0 && (
              <div className="flex items-center gap-1">
                <StarRating value={Math.round(vendor.average_rating)} size={14} />
                <span className="text-xs font-semibold text-muted-foreground">{vendor.average_rating} ({vendor.rating_count})</span>
              </div>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {sectorsDisplay.join(", ")}{areasDisplay.length > 0 && ` \u2022 ${areasDisplay.join(", ")}`}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {vendor.invite_status === "accepted" && (
            <Badge className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 gap-1">
              <CheckCircle size={12} weight="fill" /> Portal Active
            </Badge>
          )}
          {isAdmin && vendor.email && vendor.invite_status !== "accepted" && (
            <Button variant="outline" size="sm" onClick={handleInvite} data-testid="invite-supplier-btn">
              <EnvelopeSimple size={14} className="mr-1" /> Invite to portal
            </Button>
          )}
          {isAdmin && <SupplierMarketplaceInvite supplierId={vendorId} />}
          <Button variant="outline" size="sm" onClick={() => setShowAddRating(true)} data-testid="add-rating-btn">
            <Star size={14} className="mr-1" /> Ratings
          </Button>
          <Button variant="outline" size="sm" data-testid="pdf-btn">
            <FilePdf size={14} className="mr-1" /> PDF
          </Button>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => { setEditForm({ ...vendor, sectors: vendor.sectors?.length ? vendor.sectors : (vendor.trade ? vendor.trade.split(",").map(s => s.trim()).filter(Boolean) : []) }); setShowEdit(true); }} data-testid="edit-supplier-btn">
              <PencilSimple size={14} className="mr-1" /> Edit
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={handleDelete} data-testid="delete-supplier-btn">
              <Trash size={14} className="mr-1" /> Delete
            </Button>
          )}
        </div>
      </div>

      {/* ═══════ TABS ═══════ */}
      <div className="flex gap-1 border-b border-border mb-6" data-testid="supplier-tabs">
        {[
          { id: "details", label: "Details" },
          { id: "documents", label: `Documents (${docs.length})` },
          { id: "notes", label: "Notes" },
          { id: "badges", label: `Awards${badges.length ? ` (${badges.length})` : ""}` },
          { id: "timesheet", label: "Timesheet" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Latest badge ribbon — shown above the tabbed content for visibility */}
      {badges.length > 0 && (
        <div className="mb-5">
          <SupplierBadgeRibbon badge={badges[0]} data-testid="supplier-latest-badge" />
        </div>
      )}

      {/* ═══════ DETAILS TAB ═══════ */}
      {tab === "details" && (
        <div className="space-y-4 animate-fade-in">
          {/* Row 1: Contact + Company */}
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Contact Information */}
            <div className="border border-border rounded-lg p-5 bg-card" data-testid="contact-info-card">
              <div className="flex items-center gap-2 mb-4">
                <User size={18} className="text-muted-foreground" weight="duotone" />
                <h2 className="text-base font-semibold font-[Chivo]">Contact Information</h2>
              </div>
              <div className="space-y-4">
                <DetailRow icon={User} label="Contact Person" value={vendor.contact_name} />
                <DetailRow icon={EnvelopeSimple} label="Email" value={vendor.email} isLink={`mailto:${vendor.email}`} />
                <DetailRow icon={Phone} label="Phone" value={vendor.phone} isLink={`tel:${vendor.phone}`} />
              </div>
            </div>

            {/* Company Information */}
            <div className="border border-border rounded-lg p-5 bg-card" data-testid="company-info-card">
              <div className="flex items-center gap-2 mb-4">
                <Buildings size={18} className="text-muted-foreground" weight="duotone" />
                <h2 className="text-base font-semibold font-[Chivo]">Company Information</h2>
              </div>
              <div className="space-y-4">
                {sectorsDisplay.length > 0 && (
                  <div className="flex items-start gap-3">
                    <Buildings size={16} className="text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground mb-1.5">Services</p>
                      <div className="flex flex-wrap gap-1.5">
                        {sectorsDisplay.map(s => <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>)}
                      </div>
                    </div>
                  </div>
                )}
                {areasDisplay.length > 0 && (
                  <div className="flex items-start gap-3">
                    <MapPin size={16} className="text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground mb-1.5">Area's Covered</p>
                      <div className="flex flex-wrap gap-1.5">
                        {areasDisplay.map(a => <Badge key={a} variant="outline" className="text-xs">{a}</Badge>)}
                      </div>
                    </div>
                  </div>
                )}
                {vendor.address && (
                  <div className="flex items-start gap-3">
                    <MapPin size={16} className="text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Address</p>
                      <p className="text-sm">{vendor.address}</p>
                    </div>
                  </div>
                )}
                {vendor.website && (
                  <div className="flex items-start gap-3">
                    <ArrowSquareOut size={16} className="text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Website</p>
                      <a href={vendor.website.startsWith("http") ? vendor.website : `https://${vendor.website}`} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">{vendor.website}</a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: Banking + Company Registration */}
          <div className="grid gap-4 lg:grid-cols-2">
            {/* Banking Details */}
            <div className="border border-border rounded-lg p-5 bg-card" data-testid="banking-card">
              <div className="flex items-center gap-2 mb-4">
                <Bank size={18} className="text-muted-foreground" weight="duotone" />
                <h2 className="text-base font-semibold font-[Chivo]">Banking Details</h2>
              </div>
              {vendor.bank_account_name || vendor.bank_sort_code || vendor.bank_account_number ? (
                <div className="space-y-4">
                  {vendor.bank_account_name && <DetailRow icon={Bank} label="Account Name" value={vendor.bank_account_name} />}
                  <div className="grid grid-cols-2 gap-6">
                    {vendor.bank_sort_code && (
                      <div>
                        <p className="text-xs text-muted-foreground">Sort Code</p>
                        <p className="text-sm font-semibold mt-0.5">{vendor.bank_sort_code}</p>
                      </div>
                    )}
                    {vendor.bank_account_number && (
                      <div>
                        <p className="text-xs text-muted-foreground">Account Number</p>
                        <p className="text-sm font-semibold mt-0.5">{vendor.bank_account_number}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No banking details on file</p>
              )}
            </div>

            {/* Company Registration Information */}
            <div className="border border-border rounded-lg p-5 bg-card" data-testid="registration-card">
              <div className="flex items-center gap-2 mb-4">
                <Receipt size={18} className="text-muted-foreground" weight="duotone" />
                <h2 className="text-base font-semibold font-[Chivo]">Company Registration Information</h2>
              </div>
              {vendor.vat_number || vendor.company_registration_number ? (
                <div className="grid grid-cols-2 gap-6">
                  {vendor.vat_number && (
                    <div className="flex items-start gap-3">
                      <Receipt size={16} className="text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">VAT Number</p>
                        <p className="text-sm font-semibold mt-0.5">{vendor.vat_number}</p>
                      </div>
                    </div>
                  )}
                  {vendor.company_registration_number && (
                    <div className="flex items-start gap-3">
                      <FileText size={16} className="text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Company Registration Number</p>
                        <p className="text-sm font-semibold mt-0.5">{vendor.company_registration_number}</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No registration details on file</p>
              )}
            </div>
          </div>

          {/* Row 3: Compliance Status (full width) */}
          <div className="border border-border rounded-lg p-5 bg-card" data-testid="compliance-status-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShieldCheck size={18} className={isCompliant ? "text-emerald-500" : "text-amber-500"} weight="duotone" />
                <h2 className="text-base font-semibold font-[Chivo]">Compliance Status</h2>
              </div>
              {isAdmin && (
                <Button variant="outline" size="sm" onClick={() => setShowCompliance(true)} data-testid="edit-compliance-btn">
                  Edit Compliance
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2 mb-4">
              <Badge className={isCompliant ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"}>
                {isCompliant ? "Compliant" : "Non-Compliant"}
              </Badge>
              {vendor.manually_compliant && <Badge variant="outline" className="text-[10px]">Manually Verified</Badge>}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 rounded-lg bg-muted/30 border border-border">
                <p className="text-2xl font-bold font-[Chivo] text-emerald-600">{docs.length}</p>
                <p className="text-xs text-muted-foreground mt-1">Documents</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted/30 border border-border">
                <p className={`text-2xl font-bold font-[Chivo] ${docsExpiring > 0 ? "text-amber-600" : "text-emerald-600"}`}>{docsExpiring}</p>
                <p className="text-xs text-muted-foreground mt-1">Expiring Soon</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted/30 border border-border">
                <p className={`text-2xl font-bold font-[Chivo] ${docsExpired > 0 ? "text-red-600" : "text-emerald-600"}`}>{docsExpired}</p>
                <p className="text-xs text-muted-foreground mt-1">Expired</p>
              </div>
            </div>
            {vendor.compliance_verified_at && (
              <p className="text-[10px] text-muted-foreground mt-3">Last verified: {new Date(vendor.compliance_verified_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
            )}
            {vendor.compliance_notes && (
              <p className="text-xs text-muted-foreground mt-2 italic">{vendor.compliance_notes}</p>
            )}
          </div>

          {/* Ratings Summary (if any) */}
          {ratings.total_ratings > 0 && (
            <div className="border border-border rounded-lg p-5 bg-card" data-testid="ratings-summary-card">
              <div className="flex items-center gap-2 mb-4">
                <Star size={18} className="text-amber-400" weight="fill" />
                <h2 className="text-base font-semibold font-[Chivo]">Ratings Overview</h2>
                <span className="text-xs text-muted-foreground">({ratings.total_ratings} review{ratings.total_ratings !== 1 ? "s" : ""})</span>
              </div>
              <div className="grid grid-cols-5 gap-4">
                {["quality", "reliability", "communication", "pricing", "overall"].map(key => (
                  <div key={key} className="text-center p-3 rounded-lg bg-muted/30 border border-border">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">{key}</p>
                    <p className="text-lg font-bold">{ratings.averages[key]}</p>
                    <StarRating value={Math.round(ratings.averages[key])} size={12} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════ DOCUMENTS TAB ═══════ */}
      {tab === "documents" && (
        <div className="animate-fade-in">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold font-[Chivo]">Compliance Documents</h2>
            {isAdmin && (
              <Button size="sm" onClick={() => setShowAddDoc(true)} data-testid="add-document-btn">
                <Plus size={14} className="mr-1" /> Add Document
              </Button>
            )}
          </div>
          {loadingDocs ? (
            <div className="flex justify-center py-8"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : docs.length === 0 ? (
            <div className="empty-state" data-testid="no-docs">
              <FileText size={28} className="mx-auto text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">No documents uploaded yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {docs.map(d => (
                <div key={d.id} className="flex items-center justify-between p-3.5 border border-border rounded-lg bg-card row-hover" data-testid={`doc-${d.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <FileText size={18} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold truncate">{d.doc_type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</p>
                      <p className="text-[10px] text-muted-foreground">{d.document_number || "No ref"} {d.expiry_date && `- Expires: ${fmtDate(d.expiry_date)}`}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={d.computed_status} />
                    {d.verified && <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300">Verified</Badge>}
                    {d.uploaded_by_vendor && <Badge variant="outline" className="text-[10px]">Supplier uploaded</Badge>}
                    {d.file_id ? (
                      <>
                        <Button
                          variant="outline" size="sm"
                          className="h-7 text-[10px] px-2"
                          onClick={() => handleViewDoc(d)}
                          title="Open file in a new tab"
                          data-testid={`view-doc-${d.id}`}
                        >
                          <Eye size={12} className="mr-1" /> View
                        </Button>
                        <Button
                          variant="outline" size="sm"
                          className="h-7 text-[10px] px-2"
                          onClick={() => handleDownloadDoc(d)}
                          title={d.file_name ? `Download ${d.file_name}` : "Download"}
                          data-testid={`download-doc-${d.id}`}
                        >
                          <DownloadSimple size={12} className="mr-1" /> Download
                        </Button>
                      </>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge
                          variant="outline"
                          className="text-[10px] text-red-700 border-red-300 dark:text-red-300 dark:border-red-800 gap-1"
                          title={d.file_missing
                            ? "The original file is gone from storage — please ask the supplier to re-upload"
                            : "No file was attached when this document was submitted — ask the supplier to re-upload"}
                          data-testid={`file-missing-${d.id}`}
                        >
                          <Warning size={10} weight="fill" /> {d.file_missing ? "File missing" : "No file uploaded"}
                        </Badge>
                        <Button
                          size="sm" variant="outline"
                          className="h-7 text-[10px] px-2"
                          onClick={() => handleNudgeReupload(d)}
                          disabled={nudgingDocId === d.id}
                          title={d.reupload_nudged_at ? `Last sent: ${new Date(d.reupload_nudged_at).toLocaleDateString("en-GB")}` : "Send a one-click re-upload link to this supplier"}
                          data-testid={`nudge-reupload-${d.id}`}
                        >
                          <PaperPlaneTilt size={11} className="mr-1" />
                          {d.reupload_nudged_at ? "Resend link" : "Email re-upload link"}
                        </Button>
                      </div>
                    )}
                    {isAdmin && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteDoc(d.id)} data-testid={`delete-doc-${d.id}`}>
                        <Trash size={14} className="text-muted-foreground" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══════ NOTES TAB ═══════ */}
      {tab === "notes" && (
        <div className="animate-fade-in">
          <h2 className="text-sm font-semibold font-[Chivo] mb-4">Supplier Notes</h2>
          {isAdmin && (
            <div className="flex gap-2 mb-4" data-testid="add-note-form">
              <Textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note about this supplier..." className="text-xs h-16 flex-1" data-testid="note-input" />
              <Button size="sm" onClick={handleAddNote} disabled={!newNote.trim()} className="self-end" data-testid="add-note-btn">Add</Button>
            </div>
          )}
          {notes.length === 0 ? (
            <div className="empty-state" data-testid="no-notes">
              <Note size={28} className="mx-auto text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">No notes yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {notes.map(n => (
                <div key={n.id} className="p-3.5 border border-border rounded-lg bg-card" data-testid={`note-${n.id}`}>
                  <p className="text-xs whitespace-pre-wrap">{n.content}</p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[10px] text-muted-foreground">{n.created_by_name} - {new Date(n.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                    {isAdmin && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDeleteNote(n.id)} data-testid={`delete-note-${n.id}`}>
                        <Trash size={12} className="text-muted-foreground" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Ratings section within notes/detail area */}
          {ratings.ratings.length > 0 && (
            <div className="mt-8">
              <h2 className="text-sm font-semibold font-[Chivo] mb-4">Recent Ratings</h2>
              <div className="space-y-3">
                {ratings.ratings.map(r => (
                  <div key={r.id} className="p-4 border border-border rounded-lg bg-card" data-testid={`rating-${r.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <StarRating value={r.overall} size={14} />
                          <span className="text-xs font-semibold">{r.overall}/5</span>
                        </div>
                        {r.comment && <p className="text-xs text-muted-foreground mt-1">{r.comment}</p>}
                      </div>
                      {isAdmin && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteRating(r.id)} data-testid={`delete-rating-${r.id}`}>
                          <Trash size={14} className="text-muted-foreground" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-3">
                      {["quality", "reliability", "communication", "pricing"].map(key => (
                        <div key={key} className="text-center p-1.5 rounded bg-muted/50">
                          <p className="text-[9px] uppercase text-muted-foreground">{key}</p>
                          <p className="text-xs font-semibold">{r[key]}/5</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-2">{r.rated_by_name} - {new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══════ TIMESHEET TAB — the supplier's crew hours from arrive/complete stamps ═══════ */}
      {tab === "timesheet" && (
        <div className="animate-fade-in bg-card border border-border rounded-lg p-5" data-testid="supplier-timesheet-tab">
          <SupplierTimesheetPanel endpoint={`/suppliers/${vendorId}/timesheet`} jobLinkBase="" testId="admin-supplier-timesheet" />
        </div>
      )}

      {/* ═══════ AWARDS / BADGES TAB ═══════ */}
      {tab === "badges" && (
        <div className="space-y-4 animate-fade-in" data-testid="supplier-badges-tab">
          {badges.length === 0 ? (
            <div className="empty-state border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground" data-testid="no-badges">
              No "Best Supplier" awards yet. Awards are issued automatically on the 1st of each month based on completed work, SLA grade, and client ratings.
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {badges.map(b => (
                <SupplierBadgeRibbon key={b.id} badge={b} data-testid={`supplier-badge-${b.id}`} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══════ DIALOGS ═══════ */}

      {/* Add Document */}
      <Dialog open={showAddDoc} onOpenChange={(open) => { if (!open) { setDocFile(null); } setShowAddDoc(open); }}>
        <DialogContent data-testid="add-document-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Add Compliance Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* File Upload */}
            <div>
              <Label className="text-xs">Upload Document *</Label>
              <div
                className="border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors mt-1"
                onClick={() => docFileRef.current?.click()}
                data-testid="doc-file-dropzone"
              >
                {docFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText size={18} className="text-primary" />
                    <span className="text-sm font-medium">{docFile.name}</span>
                    <span className="text-[10px] text-muted-foreground">({(docFile.size / 1024).toFixed(1)} KB)</span>
                  </div>
                ) : (
                  <>
                    <UploadSimple size={24} className="mx-auto text-muted-foreground mb-1" />
                    <p className="text-xs text-muted-foreground">Click to select a file</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">PDF, Word, Images (max 20MB)</p>
                  </>
                )}
              </div>
              <input ref={docFileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.heic" onChange={e => setDocFile(e.target.files?.[0] || null)} data-testid="doc-file-input" />
            </div>
            <div>
              <Label className="text-xs">Document Type *</Label>
              <Select value={docForm.doc_type} onValueChange={v => setDocForm(p => ({ ...p, doc_type: v }))}>
                <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Reference Number</Label><Input value={docForm.document_number} onChange={e => setDocForm(p => ({ ...p, document_number: e.target.value }))} data-testid="doc-ref-input" /></div>
            <div><Label className="text-xs">Description</Label><Input value={docForm.description} onChange={e => setDocForm(p => ({ ...p, description: e.target.value }))} data-testid="doc-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Issue Date</Label><DateInput value={docForm.issue_date} onChange={e => setDocForm(p => ({ ...p, issue_date: e.target.value }))} data-testid="doc-issue-input" /></div>
              <div><Label className="text-xs">Expiry Date</Label><DateInput value={docForm.expiry_date} onChange={e => setDocForm(p => ({ ...p, expiry_date: e.target.value }))} data-testid="doc-expiry-input" /></div>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea value={docForm.notes} onChange={e => setDocForm(p => ({ ...p, notes: e.target.value }))} className="h-16 text-xs" data-testid="doc-notes-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDoc(false)}>Cancel</Button>
            <Button onClick={handleAddDoc} disabled={savingDoc} data-testid="save-document-btn">{savingDoc ? "Uploading..." : "Add Document"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Rating */}
      <Dialog open={showAddRating} onOpenChange={setShowAddRating}>
        <DialogContent data-testid="add-rating-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Rate Supplier</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {["quality", "reliability", "communication", "pricing", "overall"].map(key => (
              <div key={key} className="flex items-center justify-between">
                <Label className="text-xs capitalize">{key} *</Label>
                <StarRating value={ratingForm[key]} onChange={v => setRatingForm(p => ({ ...p, [key]: v }))} />
              </div>
            ))}
            <div><Label className="text-xs">Comment</Label><Textarea value={ratingForm.comment} onChange={e => setRatingForm(p => ({ ...p, comment: e.target.value }))} className="h-16 text-xs" placeholder="Optional feedback..." data-testid="rating-comment-input" /></div>
            <div><Label className="text-xs">Project Reference</Label><Input value={ratingForm.project_reference} onChange={e => setRatingForm(p => ({ ...p, project_reference: e.target.value }))} placeholder="e.g. PRJ-001" data-testid="rating-project-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRating(false)}>Cancel</Button>
            <Button onClick={handleAddRating} data-testid="save-rating-btn">Submit Rating</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg" data-testid="edit-supplier-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Edit Supplier</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div><Label className="text-xs">Company Name *</Label><Input value={editForm.company_name || ""} onChange={e => setEditForm(p => ({ ...p, company_name: e.target.value }))} data-testid="edit-company-input" /></div>
            <div><Label className="text-xs">Contact Person</Label><Input value={editForm.contact_name || ""} onChange={e => setEditForm(p => ({ ...p, contact_name: e.target.value }))} data-testid="edit-contact-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Email</Label><Input value={editForm.email || ""} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} data-testid="edit-email-input" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={editForm.phone || ""} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} data-testid="edit-phone-input" /></div>
            </div>
            <div><Label className="text-xs">Address</Label><Input value={editForm.address || ""} onChange={e => setEditForm(p => ({ ...p, address: e.target.value }))} data-testid="edit-address-input" /></div>
            <div><Label className="text-xs">Services</Label>
              <ServicesPicker value={editForm.sectors || []} onChange={(services) => setEditForm(p => ({ ...p, sectors: services }))} testIdPrefix="edit-services" />
            </div>
            <div><Label className="text-xs">Areas Covered</Label>
              <UkAreasPicker value={editForm.areas_covered || []} onChange={(areas) => setEditForm(p => ({ ...p, areas_covered: areas }))} testIdPrefix="edit-areas" allowAdd />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Company Reg No.</Label><Input value={editForm.company_registration_number || ""} onChange={e => setEditForm(p => ({ ...p, company_registration_number: e.target.value }))} data-testid="edit-reg-input" /></div>
              <div><Label className="text-xs">VAT Number</Label><Input value={editForm.vat_number || ""} onChange={e => setEditForm(p => ({ ...p, vat_number: e.target.value }))} data-testid="edit-vat-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Bank Account Name</Label><Input value={editForm.bank_account_name || ""} onChange={e => setEditForm(p => ({ ...p, bank_account_name: e.target.value }))} data-testid="edit-bank-name-input" /></div>
              <div><Label className="text-xs">Bank Sort Code</Label><Input value={editForm.bank_sort_code || ""} onChange={e => setEditForm(p => ({ ...p, bank_sort_code: e.target.value }))} data-testid="edit-bank-sort-input" /></div>
            </div>
            <div><Label className="text-xs">Bank Account Number</Label><Input value={editForm.bank_account_number || ""} onChange={e => setEditForm(p => ({ ...p, bank_account_number: e.target.value }))} data-testid="edit-bank-number-input" /></div>
            <div><Label className="text-xs">Website</Label><Input value={editForm.website || ""} onChange={e => setEditForm(p => ({ ...p, website: e.target.value }))} placeholder="https://..." data-testid="edit-website-input" /></div>
            <div><Label className="text-xs">Accreditations</Label><Input value={editForm.accreditations || ""} onChange={e => setEditForm(p => ({ ...p, accreditations: e.target.value }))} data-testid="edit-accreditations-input" /></div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={editForm.status || "active"} onValueChange={v => setEditForm(p => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
            <Button onClick={handleEditVendor} data-testid="save-edit-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Compliance */}
      <Dialog open={showCompliance} onOpenChange={setShowCompliance}>
        <DialogContent data-testid="edit-compliance-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Edit Compliance Status</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Manually Compliant</Label>
              <Switch checked={complianceForm.manually_compliant} onCheckedChange={v => setComplianceForm(p => ({ ...p, manually_compliant: v }))} data-testid="compliance-manual-toggle" />
            </div>
            <div>
              <Label className="text-xs">Compliance Notes</Label>
              <Textarea placeholder="Add compliance notes..." className="text-xs h-20 mt-1" value={complianceForm.compliance_notes} onChange={e => setComplianceForm(p => ({ ...p, compliance_notes: e.target.value }))} data-testid="compliance-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompliance(false)}>Cancel</Button>
            <Button onClick={handleComplianceUpdate} data-testid="save-compliance-btn">Save Compliance</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, isLink }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <Icon size={16} className="text-muted-foreground mt-0.5 shrink-0" />
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        {isLink ? (
          <a href={isLink} className="text-sm text-primary hover:underline font-medium">{value}</a>
        ) : (
          <p className="text-sm font-medium">{value}</p>
        )}
      </div>
    </div>
  );
}
